import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown/with-html';
import PropTypes from 'prop-types';
import RemarkShortcodes from 'remark-shortcodes';

import Layout from '../components/Layout';
import LogoContainer from '../components/Containers/LogoContainer';
import Logo from '../components/Logo';
import { T4D, T5D } from '../components/Text';
import Card from '../components/Card';
import WorkdetailCardContainer from '../components/Containers/WorkdetailCardContainer';
import PostCardContainer from '../components/Containers/PostCardContainer';
import WorkdetailHeader from '../components/Workdetail/Header/index';
import WorkdetailSection from '../components/Workdetail/Section';
import SEO from '../components/Seo';

import {
  linkRenderer,
  embedRenderer,
  imageRenderer,
  workdetailSectionRenderer,
  shortcodeRenderer,
  blockquoteRenderer
} from '../helpers/renderers';
import { getURL } from '../helpers/url';

import postStyle from '../styles/post';

const WorkdetailContainer = styled.div`
  ${postStyle};

  overflow-x: hidden;
  width: 100%;
`;

const WorkdetailTemplate = ({
  location: { pathname },
  data: {
    strapiWorkdetail: workdetail,
    allStrapiPost: posts,
    allFile: optimizedImages
  }
}) => (
  <>
    <SEO
      title={workdetail.title}
      description={workdetail.subtitle}
      image={
        workdetail.preview.childImageSharp
          ? workdetail.preview.childImageSharp.fluid.src
          : workdetail.preview.publicURL
      }
      pathname={pathname}
    />
    <Layout
      hasHeader
      circleColor={workdetail.circlecolor}
      {...workdetail.headerbackground && {
        circleImage: workdetail.headerbackground.publicURL
      }}
    >
      <WorkdetailHeader
        title={workdetail.title}
        subtitle={workdetail.subtitle}
        type={workdetail.headertype}
        images={[
          { src: workdetail.headerimage1, alt: workdetail.headeralt1 },
          { src: workdetail.headerimage2, alt: workdetail.headeralt2 }
        ]}
      />
      <article nopadding="true">
        <WorkdetailContainer>
          <ReactMarkdown
            source={workdetail.content}
            renderers={{
              link: linkRenderer,
              parsedHtml: embedRenderer,
              image: args => imageRenderer(args, optimizedImages.edges),
              code: args =>
                workdetailSectionRenderer(args, optimizedImages.edges),
              blockquote: blockquoteRenderer,
              shortcode: args => shortcodeRenderer(args, optimizedImages.edges)
            }}
            escapeHtml={false}
            plugins={[RemarkShortcodes]}
          />
        </WorkdetailContainer>
        {workdetail.technologies.length > 0 && (
          <WorkdetailSection>
            <T4D>Tech We Use</T4D>
            <LogoContainer>
              {workdetail.technologies.map(technology => (
                <Logo
                  key={technology.id}
                  imgSrc={technology.icon.publicURL}
                  imgAlt={`${technology.name} logo`}
                  text={technology.name}
                  center
                />
              ))}
            </LogoContainer>
          </WorkdetailSection>
        )}
        {workdetail.awards.length > 0 && (
          <WorkdetailSection>
            <T4D>Awards</T4D>
            <LogoContainer>
              {workdetail.awards.map(award => (
                <Logo
                  key={award.id}
                  imgSrc={award.icon.publicURL}
                  imgAlt={`${award.name} icon`}
                  text={award.name}
                  center
                />
              ))}
            </LogoContainer>
          </WorkdetailSection>
        )}
        {posts.edges.length > 0 && (
          <WorkdetailSection>
            <T5D as="h2">Related Thoughts</T5D>
            <PostCardContainer>
              {posts.edges.map(post => (
                <Card
                  key={post.node.id}
                  imgSrc={post.node.image}
                  imgAlt={post.node.title}
                  title={post.node.title}
                  text={post.node.shortsubtitle}
                  tags={post.node.tags}
                  link={getURL('observatory', post.node.title)}
                />
              ))}
            </PostCardContainer>
          </WorkdetailSection>
        )}
        {workdetail.related.length > 0 && (
          <WorkdetailSection>
            <T5D as="h2">Check These Out</T5D>
            <WorkdetailCardContainer>
              {workdetail.related.map(related => (
                <Card
                  key={related.id}
                  imgSrc={related.preview}
                  imgAlt={related.name}
                  title={related.title}
                  text={related.subtitle}
                  action="Learn More"
                  link={getURL('work', related.title)}
                />
              ))}
            </WorkdetailCardContainer>
          </WorkdetailSection>
        )}
      </article>
    </Layout>
  </>
);

WorkdetailTemplate.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

export default WorkdetailTemplate;

export const query = graphql`
  query WorkdetailTemplate($id: String!, $posts: [String!]) {
    strapiWorkdetail(id: { eq: $id }) {
      title
      subtitle
      circlecolor
      headertype
      headerimage1 {
        publicURL
        childImageSharp {
          fluid(maxWidth: 1000, quality: 75) {
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
      headeralt1
      headerimage2 {
        publicURL
        childImageSharp {
          fluid(maxWidth: 1000, quality: 75) {
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
      headeralt2
      content
      technologies {
        id
        name
        icon {
          publicURL
        }
      }
      awards {
        id
        name
        icon {
          publicURL
        }
      }
      related {
        id
        title
        subtitle
        preview {
          publicURL
          childImageSharp {
            fluid(maxWidth: 800, maxHeight: 500) {
              ...GatsbyImageSharpFluid_noBase64
            }
          }
        }
      }
      preview {
        publicURL
        childImageSharp {
          fluid(maxWidth: 520, cropFocus: CENTER) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
    allFile(filter: { parent: { id: { eq: $id } } }) {
      edges {
        node {
          url
          publicURL
          childImageSharp {
            fluid(maxWidth: 1000) {
              ...GatsbyImageSharpFluid_noBase64
            }
          }
        }
      }
    }
    allStrapiPost(filter: { id: { in: $posts } }) {
      edges {
        node {
          id
          title
          shortsubtitle
          tags {
            name
          }
          image {
            publicURL
            childImageSharp {
              fluid(maxWidth: 700, maxHeight: 440, cropFocus: CENTER) {
                ...GatsbyImageSharpFluid_noBase64
              }
            }
          }
        }
      }
    }
  }
`;
